import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  CloudUpload,
  InsertDriveFile,
  PictureAsPdf,
  Description,
  OpenInNew,
  Close,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchDashboardInfoRequest,
  createDashboardInfoRequest,
  clearDashboardInfo,
} from "../../../redux/DashboardInfo/DashboardInfoSlice";
import { canShowPermission } from "../../MainScreens/Permissions/permissionObject";


const PAGE_OPTIONS = [
  { label: "Dashboard", value: "DASHBOARD" },
  { label: "Metal Management", value: "METAL_MANAGEMENT" },
  { label: "Transaction & Order", value: "TRANSACTION_AND_ORDER" },
  { label: "App Management", value: "APP_MANAGEMENT" },
  { label: "Products Management", value: "PRODUCT_MANAGEMENT" },
  { label: "Plan Management", value: "PLAN_MANAGEMENT" },
  { label: "Gifts", value: "GIFT" },
  { label: "Customers", value: "CUSTOMER" },
  { label: "Admin", value: "ADMIN" },
  { label: "Store", value: "STORE" },
  { label: "Offers", value: "OFFER" },
  { label: "Jeweller", value: "JEWELLER" },
  { label: "Subscriptions", value: "SUBSCRIPTION" },
  { label: "Settings", value: "SETTING" },
  { label: "Tickets Management", value: "TICKET_MANAGEMENT" },
  { label: "Booking Bullion", value: "BOOKING_BULLION" },
  {label: "FAQs", value: "FAQ"}
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseDescription = (description) => {
  if (!description) return "";
  if (Array.isArray(description)) return description.map((d) => d.insert || "").join("");
  if (typeof description === "string" && description.startsWith('"')) return JSON.parse(description);
  return description;
};

const getFileIcon = (url) => {
  if (/\.pdf$/i.test(url)) return <PictureAsPdf sx={{ color: "#e53935" }} />;
  if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url))
    return <i className="ri-video-line" style={{ fontSize: 20, color: "#7b1fa2" }} />;
  return <Description sx={{ color: "#1565c0" }} />;
};

// ─── Editor Toolbar ───────────────────────────────────────────────────────────

const ToolbarButton = ({ onClick, active, children }) => (
  <Button
    variant="outlined"
    size="small"
    onClick={onClick}
    color={active ? "primary" : "inherit"}
    sx={{ minWidth: "36px", p: 0.5 }}
  >
    {children}
  </Button>
);

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;
  const tools = [
    { icon: "ri-bold", action: () => editor.chain().focus().toggleBold().run(), active: editor.isActive("bold") },
    { icon: "ri-italic", action: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive("italic") },
    { icon: "ri-underline", action: () => editor.chain().focus().toggleUnderline().run(), active: editor.isActive("underline") },
    { icon: "ri-strikethrough", action: () => editor.chain().focus().toggleStrike().run(), active: editor.isActive("strike") },
    { icon: "ri-align-left", action: () => editor.chain().focus().setTextAlign("left").run(), active: editor.isActive({ textAlign: "left" }) },
    { icon: "ri-align-center", action: () => editor.chain().focus().setTextAlign("center").run(), active: editor.isActive({ textAlign: "center" }) },
    { icon: "ri-align-right", action: () => editor.chain().focus().setTextAlign("right").run(), active: editor.isActive({ textAlign: "right" }) },
    { icon: "ri-align-justify", action: () => editor.chain().focus().setTextAlign("justify").run(), active: editor.isActive({ textAlign: "justify" }) },
  ];
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 2 }}>
      {tools.map(({ icon, action, active }) => (
        <ToolbarButton key={icon} onClick={action} active={active}>
          <i className={icon} />
        </ToolbarButton>
      ))}
    </Box>
  );
};

// ─── File Preview Dialog ──────────────────────────────────────────────────────

const FilePreviewDialog = ({ file, onClose }) => {
  const url = file?.path;
  const displayName = file?.filename || url?.split("/").pop();

  return(
  <Dialog open={!!url} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
      <Typography fontWeight={600} fontSize={14} noWrap>
        {displayName}
      </Typography>
      <IconButton onClick={onClose} size="small">
        <Close />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 2, textAlign: "center", minHeight: 300 }}>
      {/\.(jpg|jpeg|png|gif|webp)$/i.test(url) ? (
        <img src={url} alt="preview" style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8, objectFit: "contain" }} />
      ) : /\.pdf$/i.test(url) ? (
        <iframe src={url} title="PDF Preview" width="100%" height="500px" style={{ border: "none", borderRadius: 8 }} />
      ) : /\.(mp4|webm|ogg|mov|avi)$/i.test(url) ? (
        <video controls autoPlay style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}>
          <source src={url} />
        </video>
      ) : (
        <Box sx={{ py: 6 }}>
          <Typography color="text.secondary" mb={2}>Preview not available for this file type.</Typography>
          <Button variant="outlined" onClick={() => window.open(url, "_blank")}>Open in new tab</Button>
        </Box>
      )}
    </DialogContent>
  </Dialog>
  );
};

// ─── Existing File Item ───────────────────────────────────────────────────────

const ExistingFileItem = ({ file, onRemove, onPreview }) => {
  const { path, filename } = file;
  const displayName = filename || path.split("/").pop();
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(path);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      {isImage && (
        <Box
          sx={{
            position: "relative",
            borderRadius: "8px",
            overflow: "hidden",
            cursor: "pointer",
            "&:hover .img-overlay": { opacity: 1 },
          }}
          onClick={() => onPreview(path)}
        >
          <img src={path} alt={displayName} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, display: "block" }} />
          <Box
            className="img-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(103,58,183,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.2s",
              borderRadius: "8px",
            }}
          >
            <OpenInNew sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
        </Box>
      )}
      <Chip
        icon={<InsertDriveFile />}
        label={displayName}
        onDelete={onRemove}
        onClick={() => onPreview(file)}
        size="small"
        variant="outlined"
        color="primary"
        sx={{ cursor: "pointer" }}
      />
    </Box>
  );
};


const CreateDashboardInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedPage, setSelectedPage] = useState("DASHBOARD");
  const [existingFileUrls, setExistingFileUrls] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [permissions, setPermissions] = useState({});

  const { data, loading } = useSelector((state) => state.dashboardInfo);
  const isEditing = !!data;

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const canCreateDashboardInfo = canShowPermission(
      permissions,
      "dashboardInfo",
      "create",
    );
    const canEdit = canShowPermission(permissions, "dashboardInfo", "edit");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: "Write something here..." }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: "",
    editable: true,
  });

  // Fetch data for the default page on mount
  useEffect(() => {
    dispatch(fetchDashboardInfoRequest({ pageName: "DASHBOARD" }));
    return () => {
      dispatch(clearDashboardInfo());
    };
  }, []);

  // Populate form when data arrives
  useEffect(() => {
    if (data && editor) {
      editor.commands.setContent(parseDescription(data.description), false);
      setExistingFileUrls(Array.isArray(data.file) ? data.file.map((f) => ({ path: f.path, filename: f.filename })) : []);
    }
  }, [data, editor]);

  const handlePageChange = (e) => {
    const newPage = e.target.value;
    setSelectedPage(newPage);
    // Reset form and fetch data for newly selected page
    dispatch(clearDashboardInfo());
    editor?.commands.setContent("");
    setExistingFileUrls([]);
    setNewFiles([]);
    dispatch(fetchDashboardInfoRequest({ pageName: newPage }));
  };

  const handleFileChange = (e) => {
    setNewFiles((prev) => [...prev, ...Array.from(e.target.files)]);
  };



  const handleSave = () => {
    const hasAccess = isEditing ? canEdit : canCreateDashboardInfo;

  if (!hasAccess) {
    toast.error("You don't have permission to create or edit page information.");
    return;
  }

    const formData = new FormData();
    formData.append("pageName", selectedPage);
    const descriptionDelta = [{ insert: editor?.getHTML() || "" }];
    formData.append("description", JSON.stringify(descriptionDelta));
    if (newFiles.length === 0 && existingFileUrls.length === 0) {
      formData.append("file", JSON.stringify([]));
    } else {
      newFiles.forEach((file) => formData.append("file", file));
      existingFileUrls.forEach((file) => formData.append("file", file.path));
    }
    dispatch(createDashboardInfoRequest(formData));
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 2, borderRadius: "16px" }} elevation={2}>
    <Box sx={{ width: "100%", minHeight: "100vh", overflow: "auto" }}>
      <Box sx={{ width: "100%", }}>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            
            <Box>
              <Typography fontWeight={700} fontSize={24} fontFamily="Aeonik" lineHeight={1.2}>
                {isEditing ? "Edit Page Information" : "Add Page Information"}
              </Typography>
              <Typography fontSize={13} color="text.secondary" mt={0.25}>
                {isEditing
                  ? "Update content and attachments for the selected page."
                  : "Choose a page and add a description and attachments."}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* ── Page selector ── */}
          <TextField
            select
            label="Select Page"
            value={selectedPage}
            onChange={handlePageChange}
            fullWidth
            margin="normal"
          >
            {PAGE_OPTIONS.map((page) => (
              <MenuItem key={page.value} value={page.value}>
                {page.label}
              </MenuItem>
            ))}
          </TextField>
          
          {/* ── Rich text editor ── */}
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              mb: 2,
              mt: 2,
              "& .ProseMirror": {
                minHeight: "300px",
                padding: "12px",
                outline: "none",
                whiteSpace: "pre-wrap",
              },
              "& .ProseMirror p.is-editor-empty:first-child::before": {
                content: "attr(data-placeholder)",
                color: "#aaa",
                float: "left",
                pointerEvents: "none",
                height: 0,
              },
            }}
          >
            <EditorToolbar editor={editor} />
            <Divider />
            <EditorContent editor={editor} style={{ minHeight: "300px", padding: "12px", outline: "none", cursor: "text" }} />
          </Box>

          {/* ── Existing files ── */}
          {existingFileUrls.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
                Existing Files
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {existingFileUrls.map((file, i) => (
                  <ExistingFileItem
                    key={file.path || i}
                    file={file}
                    onRemove={() => setExistingFileUrls((prev) => prev.filter((_, idx) => idx !== i))}
                    onPreview={setPreviewFile}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* ── File upload zone ── */}
          <Box
            component="label"
            htmlFor="file-upload"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1.5px dashed",
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              cursor: "pointer",
              mb: 1.5,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <CloudUpload sx={{ fontSize: 36, color: "text.secondary", mb: 1 }} />
            <Typography fontSize={14}>
              Drop files or images here, or <strong>browse</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supports images, PDFs, docs, videos — multiple files allowed
            </Typography>
            <input
              id="file-upload"
              type="file"
              multiple
              hidden
              accept="image/*,.pdf,.doc,.docx,.txt,video/*"
              onChange={handleFileChange}
            />
          </Box>

          {/* ── New file chips ── */}
          {newFiles.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
              {newFiles.map((file, i) => (
                <Chip
                  key={i}
                  icon={<InsertDriveFile />}
                  label={file.name}
                  onDelete={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{
                backgroundColor: "#752ADA",
                px: 3,
                fontSize: "14px",
                textTransform: "none",
                height: "40px",
                "&:hover": { backgroundColor: "#5e1fc0" },
                "&:disabled": { backgroundColor: "#c4a8f0", color: "#fff" },
              }}
            >
              {loading ? "Saving..." : isEditing ? "Update" : "Save"}
            </Button>
          </Box>
      </Box>

      <FilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
    </Box>
    </Paper>
  );
};

export default CreateDashboardInfo;